import { useRef, useState, useEffect, Fragment } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BusinessCardPreviewProps } from '../../types';

// Positioning configs for each card style - defines where content block sits on the image
interface StyleConfig {
  width: number;
  height: number | 'auto';
  content: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    textAlign: 'left' | 'center' | 'right';
    color: string;
    fontSize: number;
    maxWidth?: string;
    width?: string;
  };
}

const STYLE_CONFIGS: Record<string, StyleConfig> = {
  'card-style-1': {
    width: 700, height: 400,
    content: { bottom: '15%', left: '6%', textAlign: 'left', color: '#ffffff', fontSize: 17 },
  },
  'card-style-1-left-top': {
    width: 700, height: 400,
    content: { top: '10%', left: '6%', textAlign: 'left', color: '#ffffff', fontSize: 17 },
  },
  'card-style-1-right-top': {
    width: 700, height: 400,
    content: { top: '10%', right: '6%', textAlign: 'right', color: '#ffffff', fontSize: 17 },
  },
  'card-style-1-right-bottom': {
    width: 700, height: 400,
    content: { bottom: '15%', right: '6%', textAlign: 'right', color: '#ffffff', fontSize: 17 },
  },
  'card-style-2-left-top': {
    width: 700, height: 400,
    content: { top: '10%', left: '6%', textAlign: 'left', color: '#ffffff', fontSize: 17 },
  },
  'card-style-3-left-top': {
    width: 700, height: 400,
    content: { top: '10%', left: '10%', textAlign: 'left', color: '#ffffff', fontSize: 20, width: '100%' },
  },
  'bus-card-center': {
    width: 700, height: 'auto',
    content: { top: '24%', left: '0', right: '0', textAlign: 'center', color: '#03244d', fontSize: 17, maxWidth: '500px' },
  },
  'sfb-bus-card-std': {
    width: 700, height: 'auto',
    content: { top: '16%', left: '52%', textAlign: 'center', color: '#813037', fontSize: 17, width: '274px' },
  },
  'sfb-bus-card-tx': {
    width: 700, height: 'auto',
    content: { top: '26%', left: '250px', textAlign: 'left', color: '#3e3e3e', fontSize: 18, width: 'calc(100% - 270px)' },
  },
};

const DEFAULT_CONFIG: StyleConfig = {
  width: 700, height: 400,
  content: { top: '10%', left: '6%', textAlign: 'left', color: '#ffffff', fontSize: 17 },
};

// Name-level fields: render large, bold, uppercase
const NAME_FIELDS = ['company_name', 'agency_name', 'individual_name', 'agency_combine', 'property'];

// City/state/zip/country render inline on one line
const INLINE_FIELDS = ['city', 'state', 'zip_code', 'country'];

// Meta fields (group toggles) - never rendered as fields
const META_FIELDS = ['is_saved_address', 'address_group'];

// slight_bar is a visual-only separator with no location value
const VISUAL_ONLY_FIELDS = ['slight_bar'];

// title2 renders like title but without top margin (it follows directly after title)
const TITLE_FIELDS = ['title', 'title2'];

// credentials renders inline after individual_name (appended with comma)
// country renders like a regular address field

// Standard phone/contact prefixes matching font-preview-field-* CSS ::before
const PHONE_PREFIXES: Record<string, string> = {
  phone: 'T ',
  cell_phone: 'C ',
  fax: 'F ',
  email: 'E ',
  customer_service_phone: 'CS ',
  toll_free: 'TF ',
};

// bus-card-center and sfb-* styles use O/M instead of T/C
const MULTI_PHONE_STYLES = ['bus-card-center', 'sfb-bus-card-std', 'sfb-bus-card-tx'];
const MULTI_PHONE_PREFIXES: Record<string, string> = {
  phone: 'O ',
  cell_phone: 'M ',
  fax: 'F ',
};

export function BusinessCardPreview({ productImages, previewImage, data, style, fields, fontColor }: BusinessCardPreviewProps) {
  const fieldKeys = fields.map(f => f.key);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [downloading, setDownloading] = useState(false);

  const imageUrl = previewImage || productImages?.[0];
  const config = (style && STYLE_CONFIGS[style]) || DEFAULT_CONFIG;
  const isMultiPhone = style ? MULTI_PHONE_STYLES.includes(style) : false;
  const isStyle3 = style === 'card-style-3-left-top';
  const isBusCardCenter = style === 'bus-card-center';
  const isSfbStd = style === 'sfb-bus-card-std';
  const isSfbTx = style === 'sfb-bus-card-tx';

  useEffect(() => {
    if (!containerRef.current) return;
    const updateScale = () => {
      const containerWidth = containerRef.current?.offsetWidth || config.width;
      setScale(containerWidth / config.width);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [config.width]);

  if (!imageUrl) return null;

  const effectiveColor = fontColor || config.content.color;
  const baseFontSize = config.content.fontSize * scale;

  const contentStyle: React.CSSProperties = {
    position: 'absolute',
    textAlign: config.content.textAlign,
    color: effectiveColor,
    fontSize: `${baseFontSize}px`,
    lineHeight: 1.4,
    ...(config.content.top !== undefined && { top: config.content.top }),
    ...(config.content.bottom !== undefined && { bottom: config.content.bottom }),
    ...(config.content.left !== undefined && { left: config.content.left }),
    ...(config.content.right !== undefined && { right: config.content.right }),
    ...(config.content.maxWidth && { maxWidth: config.content.maxWidth, marginLeft: 'auto', marginRight: 'auto' }),
    ...(config.content.width && { width: config.content.width }),
  };

  // slight_bar always renders (visual only); all other fields need a location value
  const baseRenderableFields = fieldKeys.filter(f =>
    !META_FIELDS.includes(f) &&
    (VISUAL_ONLY_FIELDS.includes(f) || data[f])
  );

  // bus-card-center & sfb-bus-card-std: hardcoded field order — name/title → address → phones → email/web
  const ORDERED_FIELD_STYLES_ORDER = [
    'company_name', 'agency_name', 'agency_combine', 'individual_name', 'property',
    'title', 'title2', 'credentials', 'division', 'function', 'location',
    'slight_bar',
    'address', 'address2', 'city', 'state', 'zip_code', 'country',
    'phone', 'cell_phone', 'fax', 'customer_service_phone', 'toll_free',
    'email', 'other_info', 'web_address', 'additional_agents', 'optional_text1', 'optional_text2',
  ];

  const usesHardcodedOrder = isBusCardCenter || isSfbStd || isSfbTx;

  // For styles with address_group: individual address fields aren't in the fields prop
  // but the user fills them in the form — add them to renderableFields if they have values.
  const ADDRESS_FIELDS_LIST = ['address', 'address2', 'city', 'state', 'zip_code'];
  const extraAddressFields = usesHardcodedOrder &&
    (fieldKeys.includes('address_group') || fieldKeys.includes('is_saved_address'))
    ? ADDRESS_FIELDS_LIST.filter(f => data[f] && !baseRenderableFields.includes(f))
    : [];

  const allRenderableFields = [...baseRenderableFields, ...extraAddressFields];

  const renderableFields = usesHardcodedOrder
    ? [...allRenderableFields].sort((a, b) => {
        const ia = ORDERED_FIELD_STYLES_ORDER.indexOf(a);
        const ib = ORDERED_FIELD_STYLES_ORDER.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      })
    : allRenderableFields;

  // Break-line logic: mirrors the old "break-line-phone-field" mechanism.
  // When 2+ phone fields are filled, insert a block break before the first phone
  // AND before the address field so each group sits on its own visual line.
  const MULTI_PHONE_FIELDS = ['phone', 'cell_phone', 'fax'];
  const firstPhoneField = renderableFields.find(f => MULTI_PHONE_FIELDS.includes(f)) ?? null;

  const getPrefix = (field: string): string => {
    if (isMultiPhone && MULTI_PHONE_PREFIXES[field]) return MULTI_PHONE_PREFIXES[field];
    return PHONE_PREFIXES[field] || '';
  };

  const handleDownloadPDF = async () => {
    if (!containerRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(containerRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        logging: false,
      });
      // Standard business card: 3.5" × 2" in mm
      const cardWidthMm = 88.9;
      const cardHeightMm = 50.8;
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [cardWidthMm, cardHeightMm],
      });
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, cardWidthMm, cardHeightMm);
      pdf.save('business-card.pdf');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: `${config.width}px` }}>
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-lg border border-gray-200 shadow-sm"
      style={{ width: '100%' }}
    >
      <img
        src={imageUrl}
        alt="Business Card Preview"
        className="w-full h-auto block"
      />
      <div style={contentStyle}>
        {renderableFields.map(field => {
          // font-preview-field-slight_bar: horizontal separator
          if (field === 'slight_bar') {
            // card-style-3 / bus-card-center / sfb-std: 50px, 10px margin (vs 60px / 8px default)
            const smallBar = isStyle3 || isBusCardCenter || isSfbStd;
            const barWidth = smallBar ? 50 : 60;
            const barMargin = smallBar ? 10 : 8;
            return (
              <div
                key={field}
                style={{
                  width: `${barWidth * scale}px`,
                  margin: `${barMargin * scale}px 0`,
                  borderTop: `1px solid ${effectiveColor}`,
                }}
              />
            );
          }

          // font-preview-field-{name}: large, bold, uppercase
          if (NAME_FIELDS.includes(field)) {
            // card-style-3: company_name is italic+capitalize; others are uppercase+bold
            const isCompany = field === 'company_name';
            const style3Override = isStyle3 && isCompany
              ? { fontStyle: 'italic' as const, textTransform: 'capitalize' as const }
              : {};
            // bus-card-center: 25px; agency_name/agency_combine/individual_name get -5px top margin
            const CENTER_NEGATIVE_MARGIN_FIELDS = ['agency_name', 'agency_combine', 'individual_name'];
            const busCardCenterOverride = isBusCardCenter && CENTER_NEGATIVE_MARGIN_FIELDS.includes(field)
              ? { marginTop: `${-5 * scale}px`, marginBottom: '0' }
              : {};
            // sfb-bus-card-std: company_name=25px; agency/individual/property/agency_combine=26px+margin+lineHeight
            const SFB_STD_LARGE_FIELDS = ['agency_name', 'agency_combine', 'property', 'individual_name'];
            const sfbStdOverride = isSfbStd && SFB_STD_LARGE_FIELDS.includes(field)
              ? { fontSize: `${26 * scale}px`, marginTop: `${10 * scale}px`, lineHeight: `${35 * scale}px` }
              : {};
            // sfb-bus-card-tx: ALL name fields 26px, huge margin-top 160px to push block down the card
            const sfbTxOverride = isSfbTx
              ? { fontSize: `${26 * scale}px`, marginTop: `${160 * scale}px`, lineHeight: `${22 * scale}px`, maxWidth: `${400 * scale}px` }
              : {};
            const nameFontSize = (isBusCardCenter || isSfbStd || isSfbTx) ? 25 : 30;
            return (
              <div key={field} style={{ fontSize: `${nameFontSize * scale}px`, fontWeight: 'bold', textTransform: 'uppercase', ...style3Override, ...busCardCenterOverride, ...sfbStdOverride, ...sfbTxOverride }}>
                {data[field]}
              </div>
            );
          }

          // title / title2: bold, uppercase; title gets a top gap, title2 follows directly
          if (TITLE_FIELDS.includes(field)) {
            // card-style-3: title has no top margin
            // bus-card-center: title has large bottom margin (50px) to separate from phone block
            // sfb-bus-card-std: title has 10px top margin
            const titleMarginTop = field === 'title'
              ? isStyle3 ? undefined : (isSfbStd || isSfbTx) ? `${10 * scale}px` : `${6 * scale}px`
              : undefined;
            const titleMarginBottom = (isBusCardCenter && field === 'title') ? `${50 * scale}px` : undefined;
            return (
              <div key={field} style={{ fontWeight: 'bold', textTransform: 'uppercase', marginTop: titleMarginTop, marginBottom: titleMarginBottom }}>
                {data[field]}
              </div>
            );
          }

          // credentials: italic, follows name/title block
          if (field === 'credentials') {
            return (
              <div key={field} style={{ fontStyle: 'italic' }}>
                {data[field]}
              </div>
            );
          }

          // division / function / location: regular text, small top gap to visually group
          if (field === 'division' || field === 'function' || field === 'location') {
            return (
              <div key={field} style={{ marginTop: `${4 * scale}px` }}>
                {data[field]}
              </div>
            );
          }

          // font-preview-field-address: small top gap after slight_bar
          if (field === 'address') {
            // card-style-3: large 15% top margin to push address block down the card
            // bus-card-center: inline-block with ", " suffix so it flows with city/state/zip on one line.
            // A block break is inserted before it (when multi-phone) so this group starts on its own line.
            if (isBusCardCenter) {
              return (
                <Fragment key={field}>
                  <div style={{ display: 'block', width: '100%', height: 0 }} />
                  <span style={{ display: 'inline-block' }}>
                    {data[field]},{' '}
                  </span>
                </Fragment>
              );
            }
            return (
              <div key={field} style={{ marginTop: isStyle3 ? '15%' : isSfbTx ? `${5 * scale}px` : `${6 * scale}px`, display: isStyle3 ? 'inline-block' : undefined }}>
                {data[field]}
              </div>
            );
          }

          // font-preview-field-city/state/zip_code: inline on one line
          // card-style-3: city uses "| city," format (pipe separator before)
          // others: "city," with padding on state/zip
          if (INLINE_FIELDS.includes(field)) {
            if (isStyle3 && field === 'city') {
              return (
                <span key={field} style={{ display: 'inline-block' }}>
                  <span style={{ padding: `0 ${10 * scale}px` }}>|</span>
                  {data[field]},
                </span>
              );
            }
            return (
              <span key={field} style={{ paddingLeft: field !== 'city' ? `${4 * scale}px` : undefined }}>
                {data[field]}{field === 'city' ? ',' : ''}
              </span>
            );
          }

          // bus-card-center: web_address with special styling
          if (isBusCardCenter && field === 'web_address') {
            return (
              <div key={field} style={{ marginTop: `${20 * scale}px`, textTransform: 'uppercase', fontSize: '0.8em', color: '#cfcbc1' }}>
                {data[field]}
              </div>
            );
          }

          // font-preview-field-phone/cell_phone/fax/email: with T/C/F/E prefix
          // phone gets a top gap to separate contact block from address block
          // card-style-3 email: padding 5px 0
          const prefix = getPrefix(field);
          const isEmailStyle3 = isStyle3 && field === 'email';
          // card-style-3 phone top gap is 10px; others use 12px
          const phoneTopGap = field === 'phone' ? (isStyle3 ? `${10 * scale}px` : `${12 * scale}px`) : undefined;
          // multi-phone styles: phone/cell_phone/fax render inline with smaller font.
          // A block break before the first phone starts their group on its own line.
          // sfb-bus-card-std uses "O: number" format (bold label + normal colon-space separator).
          // bus-card-center and sfb-bus-card-tx use "O number" (space in prefix, no colon).
          if (isMultiPhone && MULTI_PHONE_FIELDS.includes(field)) {
            const needsBreak = (isBusCardCenter || isSfbStd || isSfbTx) && field === firstPhoneField;
            // sfb-tx gap matches sfb-std gap (10px); bus-card-center gets no gap (0)
            const breakHeight = (isSfbStd || isSfbTx) ? `${10 * scale}px` : 0;
            // sfb-std: "O: number" (bold label + normal colon-space); others: "O number" (space prefix)
            const SFB_STD_LABELS: Record<string, string> = { phone: 'O', cell_phone: 'M', fax: 'F' };
            // sfb-tx uses wider padding-right (8px) vs others (5px)
            const phonePadRight = isSfbTx ? `${8 * scale}px` : `${5 * scale}px`;
            return (
              <Fragment key={field}>
                {needsBreak && <div style={{ display: 'block', width: '100%', height: breakHeight }} />}
                <span style={{ display: 'inline-block', paddingRight: phonePadRight, fontSize: `${16 * scale}px` }}>
                  {isSfbStd ? (
                    SFB_STD_LABELS[field] && (
                      <><strong>{SFB_STD_LABELS[field]}</strong><span>: </span></>
                    )
                  ) : (
                    prefix && <strong>{prefix}</strong>
                  )}
                  {data[field]}
                </span>
              </Fragment>
            );
          }
          return (
            <div key={field} style={{ marginTop: phoneTopGap, ...(isEmailStyle3 ? { padding: `${5 * scale}px 0` } : {}) }}>
              {prefix && <span style={{ fontWeight: isMultiPhone ? 'bold' : undefined }}>{prefix}</span>}
              {data[field]}
            </div>
          );
        })}
      </div>
    </div>
    <div className="mt-3 flex justify-end">
      <button
        onClick={handleDownloadPDF}
        disabled={downloading}
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {downloading ? 'Generating PDF…' : 'Download PDF'}
      </button>
    </div>
    </div>
  );
}
